import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonCol,
  IonGrid,
  IonInput,
  IonList,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonToggle,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBlurNotifierDirective } from '@okcargo-apps/layout-ionic';
import { getEmptyFreightDetailForShipper, IFreightDetailedForShipper } from '@okcargo-apps/shared-domain';
import { ICostCenter } from '@okcargo-apps/shared-domain-ionic';
import { map, Observable, Subscription, take } from 'rxjs';
import { FreightShipperService } from '../freight-shipper.service';

@Component({
  selector: 'lib-freight-shipper-edit-general-form',
  templateUrl: './freight-shipper-edit-general-form.component.html',
  styleUrls: ['./freight-shipper-edit-general-form.component.scss'],
  imports: [
    IonList,
    CommonModule,
    TranslateModule,
    IonTextarea,
    IonToggle,
    IonInput,
    IonCol,
    IonRow,
    IonGrid,
    IonSelect,
    IonSelectOption,
    ReactiveFormsModule,
    FormBlurNotifierDirective,
  ],
})
export class FreightShipperEditGeneralFormComponent implements OnInit {
  @Input() set freight(freight: IFreightDetailedForShipper) {
    this._freight.set(freight);

    this.populateForm();
  }

  @Output() dataModified = new EventEmitter<any>();

  @ViewChild('blurNotifier') blurNotifier!: FormBlurNotifierDirective;

  private readonly fb = inject(FormBuilder);
  private readonly freightShipService = inject(FreightShipperService);
  private readonly translate = inject(TranslateService);

  private readonly subscriptions = new Subscription();

  private readonly _freight = signal<IFreightDetailedForShipper>(getEmptyFreightDetailForShipper());

  public readonly costCenters$: Observable<ICostCenter[]> = this.freightShipService.costCenters$;

  public generalDataForm: FormGroup;

  public showTemperatureFields = false;

  constructor() {
    this.generalDataForm = this.createForm();

    // TODO
    /*
      Cargador es siempre el usuario activo, no hace falta en el componente
      Tipo de carga siempre es carga completa, no hace falta en el componente
     */
  }

  ngOnInit(): void {
    this.costCenters$
      .pipe(
        map((costCenters) => {
          // TODO select first element only if it is a new freight
          if (costCenters && costCenters.length > 0) {
            this.generalDataForm.patchValue({
              shipperCostCenter: costCenters[0].id,
            });
          }
        }),
      )
      .subscribe();

    this.generalDataForm.get('transportType')?.valueChanges.subscribe((types: string[]) => {
      this.updateShowTemperatureFields(types);
    });
  }

  public validateForm(): boolean {
    if (this.generalDataForm.valid) {
      return true;
    }
    this.generalDataForm.markAllAsTouched();
    Object.values(this.generalDataForm.controls).forEach((control) => {
      control.markAsDirty();
    });
    return false;
  }

  public emitModifiedData(): void {
    const formValues = this.generalDataForm.value;

    const currentFreight = this._freight();

    const updatedFreight: IFreightDetailedForShipper = {
      ...currentFreight,
      reference: formValues.shipperFreightId,
      vehicleTypes: formValues.transportType || [],
      loadingTypes: formValues.loadingType || [],
      deliveryNoteInPaperRequired: formValues.deliveryNoteInPaper || false,
      waste: formValues.field_waste || false,
      instructions: formValues.instructions || '',
      // TODO implement this ???
      shipperSiteId: formValues.shipperCostCenter,
    };

    // TODO implement this
    // if (
    //   this.showTemperatureFields &&
    //   formValues.temperatureMin !== undefined &&
    //   formValues.temperatureMax !== undefined
    // ) {
    //   updatedFreight.temperatureMin = formValues.temperatureMin;
    //   updatedFreight.temperatureMax = formValues.temperatureMax;
    // }

    this.dataModified.emit(updatedFreight);
  }

  public resetForm(): void {
    this.generalDataForm = this.createForm();

    this.showTemperatureFields = false;

    // Recargar el centro de coste por defecto
    this.costCenters$
      .pipe(
        take(1),
        map((costCenters) => {
          if (costCenters && costCenters.length > 0) {
            this.generalDataForm.patchValue({
              shipperCostCenter: costCenters[0].id,
            });
          }
        }),
      )
      .subscribe();

    // Volver a suscribirse a los cambios del tipo de transporte
    this.generalDataForm.get('transportType')?.valueChanges.subscribe((types: string[]) => {
      this.updateShowTemperatureFields(types);
    });
  }

  private createForm(): FormGroup {
    return (this.generalDataForm = this.fb.group({
      shipperCostCenter: [''],
      shipperFreightId: ['', Validators.required],
      transportType: ['', Validators.required],
      loadingType: ['', Validators.required],
      deliveryNoteInPaper: [false],
      field_waste: [false],
      instructions: [''],
    }));
  }

  private updateShowTemperatureFields(transportTypes: string[]): void {
    const isOnlyRefrigerated = transportTypes && transportTypes.length === 1 && transportTypes[0] === 'REFRIGERATED';

    this.showTemperatureFields = isOnlyRefrigerated;

    if (isOnlyRefrigerated) {
      this.generalDataForm.addControl('temperatureMin', new FormControl(null, Validators.required));
      this.generalDataForm.addControl('temperatureMax', new FormControl(null, Validators.required));
    } else {
      this.generalDataForm.removeControl('temperatureMin');
      this.generalDataForm.removeControl('temperatureMax');
    }
  }

  private populateForm(): void {
    const freight = this._freight();

    if (!freight) {
      this.generalDataForm.reset();
      this.costCenters$
        .pipe(
          map((costCenters) => {
            if (costCenters && costCenters.length > 0) {
              this.generalDataForm.patchValue({
                shipperCostCenter: costCenters[0].id,
              });
            }
          }),
        )
        .subscribe();
      return;
    }

    this.updateShowTemperatureFields(freight.vehicleTypes || []);

    const formData: any = {
      // TODO review this ????
      shipperFreightId: freight.reference || '',
      transportType: freight.vehicleTypes || [],
      loadingType: freight.loadingTypes || [],
      deliveryNoteInPaper: freight.deliveryNoteInPaperRequired || false,
      field_waste: freight.waste || false,
      instructions: freight.instructions || '',
    };

    // TODO review this, implement?
    // if (this.generalDataForm.contains('temperatureMin') && this.generalDataForm.contains('temperatureMax')) {
    //   formData.temperatureMax = freight.temperatureMax || null;
    //   formData.temperatureMin = freight.temperatureMin || null;
    // }

    this.generalDataForm.patchValue(formData);
  }
}
